import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { Card, CardBody, Col } from "react-bootstrap";
import Select from "react-select";
import { IJobCategory } from "../../shared/interfaces/job-category/IJobCategory";
import { JobCategoryService } from "./service";
import { CustomPagination } from "../../shared/components/CustomPagination";
import { usePagination } from "../../shared/hooks/usePagination";
import DeleteModal from "../../Common/DeleteModal";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const PER_PAGE_DEFAULT = 20;

interface SortableRowProps {
    item: IJobCategory;
    onEdit: (id: number) => void;
    onDelete: (id: number) => void;
    onCopy: (id: number) => void;
    onToggleCommon: (item: IJobCategory) => void;
    t: (key: string) => string;
}

const SortableRow = ({ item, onEdit, onDelete, onCopy, onToggleCommon, t }: SortableRowProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.id! });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <tr ref={setNodeRef} style={style} {...attributes}>
            <td {...listeners} style={{ cursor: "grab" }}>
                <i className="ti ti-grip-vertical f-18"></i>
            </td>
            <td>{item.name}</td>
            <td>
                <div className="form-check form-switch">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={item.isCommon}
                        onChange={() => onToggleCommon(item)}
                    />
                </div>
            </td>
            <td>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-"}</td>
            <td>{item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : "-"}</td>
            <td className="text-end">
                <ul className="list-inline mb-0">
                    <li className="list-inline-item" onClick={() => onEdit(item.id!)}>
                        <a href="#" className="avtar avtar-s btn-link-success btn-pc-default">
                            <i className="ti ti-edit f-20"></i>
                        </a>
                    </li>
                    <li className="list-inline-item" onClick={() => item.id && onCopy(item.id)}>
                        <a href="#" className="avtar avtar-s btn-link-success btn-pc-default">
                            <i className="ti ti-copy f-20"></i>
                        </a>
                    </li>
                    <li className="list-inline-item">
                        <span
                            role="button"
                            className="avtar avtar-s btn-link-danger btn-pc-default"
                            onClick={() => item.id && onDelete(item.id)}
                        >
                            <i className="ti ti-trash f-20"></i>
                        </span>
                    </li>
                </ul>
            </td>
        </tr>
    );
};

const JobCategoryList = () => {
    const { t } = useTranslation();
    const [list, setList] = useState<IJobCategory[]>([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<number | null>(null);
    const navigate = useNavigate();

    const {
        page,
        handlePages,
        pagination,
        totalPages,
        setTotalPages,
        changeFilterHandler,
        goToPreviousPage,
        goToNextPage,
    } = usePagination({ perPage: PER_PAGE_DEFAULT });

    const entriesPerPageOptions = [
        { label: "10", value: 10 },
        { label: "25", value: 25 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
        { label: "200", value: 200 },
    ];

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id && list) {
            const oldIndex = list.findIndex((item) => item.id === active.id);
            const newIndex = list.findIndex((item) => item.id === over.id);
            const newList = arrayMove(list, oldIndex, newIndex);
            setList(newList);
            try {
                await JobCategoryService.sort(newList.map((item) => item.id!));
                SuccessToast(t("jobCategory.sortSuccess"));
            } catch (error) {
                ErrorToast(error);
                fetchData();
            }
        }
    };

    const fetchData = () => {
        JobCategoryService.getAll(pagination)
            .then((response) => {
                setList(response.data);
                setTotalPages(response.totalPages);
            })
            .catch(ErrorToast);
    };

    useEffect(() => {
        fetchData();
    }, [pagination]);

    const handleDeleteClick = (id: number) => {
        setItemToDelete(id);
        setShowDeleteModal(true);
    };

    const handleCopyClick = (id: number) => {
        JobCategoryService.copy(id)
            .then(() => {
                SuccessToast(t("jobCategory.successCopy"));
                fetchData();
            })
            .catch(ErrorToast);
    };

    const handleDeleteConfirm = () => {
        if (itemToDelete !== null) {
            JobCategoryService.delete(itemToDelete)
                .then(() => {
                    SuccessToast(t("jobCategory.deleteSuccess"));
                    setShowDeleteModal(false);
                    setItemToDelete(null);
                    fetchData();
                })
                .catch(ErrorToast);
        }
    };

    return (
        <React.Fragment>
            <Col>
                <Card>
                    <CardBody>
                        <div className="text-end">
                            <button
                                className="btn btn-primary text-end"
                                onClick={() => navigate("/job-categories/create")}
                            >
                                <i className="ti ti-plus f-18"></i> {t("jobCategory.add")}
                            </button>
                        </div>

                        <div className="d-sm-flex align-items-center mb-3">
                            <ul className="list-inline me-auto my-1">
                                <li className="list-inline-item">
                                    <Select
                                        placeholder={t("jobCategory.entriesPerPage")}
                                        options={entriesPerPageOptions}
                                        onChange={(selected) =>
                                            changeFilterHandler(
                                                "perPage",
                                                selected?.value ?? PER_PAGE_DEFAULT,
                                            )
                                        }
                                        defaultValue={entriesPerPageOptions.find(
                                            (option) => option.value === PER_PAGE_DEFAULT,
                                        )}
                                        classNamePrefix="react-select"
                                    />
                                </li>
                                <span> {t("jobCategory.entriesPerPage")}</span>
                            </ul>
                        </div>

                        <div className="table-responsive">
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <table className="table table-hover" id="pc-dt-simple-1">
                                    <thead>
                                        <tr>
                                            <th style={{ width: "50px" }}></th>
                                            <th>{t("jobCategory.name")}</th>
                                            <th>{t("jobCategory.commonJob")}</th>
                                            <th>{t("jobCategory.createdAt")}</th>
                                            <th>{t("jobCategory.updatedAt")}</th>
                                            <th className="text-end">{t("jobCategory.actions")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {list.length > 0 && (
                                            <SortableContext
                                                items={list.map((item) => item.id!)}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                {list.map((item: IJobCategory) => (
                                                    <SortableRow
                                                        key={item.id}
                                                        item={item}
                                                        onEdit={(id) => navigate(`/job-categories/edit/${id}`)}
                                                        onDelete={handleDeleteClick}
                                                        onCopy={handleCopyClick}
                                                        onToggleCommon={async (item) => {
                                                            try {
                                                                await JobCategoryService.update(
                                                                    { ...item, isCommon: !item.isCommon },
                                                                    item.id!,
                                                                );
                                                                SuccessToast(t("jobCategory.successUpdate"));
                                                                fetchData();
                                                            } catch (error) {
                                                                ErrorToast(error);
                                                            }
                                                        }}
                                                        t={t}
                                                    />
                                                ))}
                                            </SortableContext>
                                        )}
                                    </tbody>
                                </table>
                            </DndContext>
                        </div>

                        <CustomPagination
                            page={page}
                            totalPages={totalPages}
                            handlePages={handlePages}
                            goToPreviousPage={goToPreviousPage}
                            goToNextPage={goToNextPage}
                        />
                    </CardBody>
                </Card>
            </Col>

            <DeleteModal
                show={showDeleteModal}
                handleClose={() => setShowDeleteModal(false)}
                handleDeleteId={handleDeleteConfirm}
                name={t("jobCategory.name")}
            />
        </React.Fragment>
    );
};

export default JobCategoryList;
